import { useEffect, useState } from 'react';
import { call, getToken } from './api.js';
import { withReturn, hereNow } from './gate.jsx';
import { TabBar, Stars } from './ui.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * YOUR REVIEWS — `08d_Reviews.html`
 *
 * ⚠️ THE SCREEN WAS LOCKED AND NOTHING COULD FILL IT. Five functions on the
 * reviews module, every one of them the restaurant's side or the public list
 * for a single venue. `GET /reviews/mine` was added the same night this was
 * built.
 *
 * ── ⚠️ WHY THIS SCREEN WILL BE EMPTY FOR A WHILE, AND THAT IS CORRECT ───────
 *
 * A review needs `visits.confidence = 'confirmed'`. The ONLY thing that writes
 * that is `eleat_confirm_attendance()` — called when a restaurant marks, in
 * Restaurant Admin, that the Eleater actually dined. A self-report never
 * qualifies; reviews.service.js says so on the line itself.
 *
 * Restaurant Admin does not exist yet. So no attendance is ever confirmed, no
 * verified visit is ever created, and nobody can write a review. **The empty
 * state here is not a bug — it is the review gate working**, and it stays that
 * way until a restaurant can mark somebody as having dined.
 *
 * ⚠️⚠️ AND THIS FILE CLAIMED "EVERY RATING IN ELEAT IS NULL TODAY". IT IS NOT.
 * Corrected 27 September 2026 by asking the live server rather than trusting
 * the line: `GET /discovery/search?market=hong-kong&sort=rating` returns
 * **three venues with a shown rating** — AMMO 4.80, LockCha Hong Kong Park
 * 4.80, Ruam 4.40, five reviews each. They are SAMPLE venues with seeded
 * aggregates (RULE ZERO-B), and they leave with the wipe.
 *
 * **The paragraph above is still true and is the point:** no Eleater can write
 * a review, because `eleat_confirm_attendance()` needs a Restaurant Admin that
 * does not exist. What was false was the extra claim I hung off it — and it
 * mattered, because it made the star renderers look like a problem for later.
 * They were drawing on Al's phone the whole time: 4.80 under the old venue
 * header rounded to **five full stars**, and 4.40 to **four**, which is exactly
 * the fault he named on 29 August. **A status line is only as good as the last
 * time somebody proved it** — this file records that six times over.
 *
 * ── A HIDDEN REVIEW IS STILL SHOWN TO ITS AUTHOR ────────────────────────────
 *
 * ⚠️ `status` comes back rather than being filtered out. A review taken down
 * by moderation must still be visible to the person who wrote it, or they are
 * left wondering where it went — and Eleat has told them nothing. They see
 * their own; they see nobody else's.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ THE FOUR SUB-SCORES — `08d_Reviews.html` DRAWS THEM AND THIS SCREEN
 * DREW NONE.
 *
 * The locked script builds `.subs` from
 * `[['Food',…],['Service',…],['Atmosphere',…],['Value',…]]`, **one chip per
 * score that is not null**, the label in muted cream and the number in
 * persimmon. This screen showed the overall stars and the body text and threw
 * the other four away — the identical fault the venue's own Reviews pane had
 * on 24 September, where `summary.cards` was fetched and discarded.
 *
 * ⚠️ AND THE LABEL IS **Ambiance**, NOT Atmosphere — WHICH IS A REAL
 * DISAGREEMENT BETWEEN TWO LOCKED SCREENS, SETTLED BY AL'S OWN RULE.
 * `08d_Reviews.html` says `Atmosphere`. `11_Restaurant.html`, the column
 * `rating_ambiance`, the API key and **the glossary** all say ambiance —
 * `eleat_glossary.json`: *"overall stars plus food, service, ambiance and
 * value."*  CLAUDE.md: *"Where a screen and the glossary disagree on what a
 * thing should be called, the glossary wins."*  So this is not a contradiction
 * to hand to Al; it is one his rule already decides, and the outlier is that
 * one word on that one screen.
 * ══════════════════════════════════════════════════════════════════════════ */
const SUBS = [
  ['Food', 'rating_food'],
  ['Service', 'rating_service'],
  ['Ambiance', 'rating_ambiance'],
  ['Value', 'rating_value'],
];

export default function Reviews() {
  const signedIn = !!getToken();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    call('/reviews/mine', { auth: true })
      .then((d) => { if (live) setRows(d.reviews || []); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [signedIn]);

  if (!signedIn) {
    return (
      <div className="home">
        <div className="top">
          <a className="back" href="?profile=1" aria-label="Back">‹</a>
          <h1 className="heroline">Your reviews</h1>
        </div>
        <main>
          <>
          <p className="mut">
            Sign in to see your reviews.
          </p>
          <div className="guest">
            <a className="gcta" href={withReturn('?welcome=1', hereNow())}>Sign in or join Eleat</a>
          </div>
          </>
        </main>
        <TabBar active="profile" />
      </div>
    );
  }

  return (
    <div className="home revlist">
      <div className="top">
        <a className="back" href="?profile=1" aria-label="Back">‹</a>
        <h1 className="heroline">Your reviews</h1>
      </div>

      <main>
        {err && <div className="err" aria-live="polite">{err}</div>}
        {rows === null && !err && <p className="mut">Loading…</p>}

        {rows !== null && rows.length === 0 && (
          <div className="revempty">
            {/* ⚠️⚠️ THE LOCKED SCREEN'S OWN EMPTY STATE, WHICH THIS DID NOT USE.
                `08d_Reviews.html` draws a 40px outlined STAR, then *"No reviews
                yet"*, then **"After a verified visit, share a review to help
                others find great food."**
                ⚠️ I HAD SUBSTITUTED HELP'S SENTENCE INSTEAD and written a
                comment defending it. Help explains the RULE; this screen tells
                somebody what to DO next, which is what an empty screen is for —
                and it is the copy that was approved for this screen. RULE 4:
                the locked screen's own words, not a better sentence from
                somewhere else. */}
            <div className="revempty-ic" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" stroke="currentColor"
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
                <path d="M12 3.2l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.6l-5.2 2.7 1-5.8L3.6 9.3l5.8-.8z" />
              </svg>
            </div>
            <div className="revempty-t">No reviews yet</div>
            <div className="revempty-s">
              After a verified visit, share a review to help others find great food.
            </div>
          </div>
        )}

        {(rows || []).map((r) => (
          <div key={r.id} className="rrow">
            <a className="rname" href={`?venue=${encodeURIComponent(r.restaurant_id)}&tab=reviews`}>
              {r.restaurant_name}
            </a>
            <div className="ratingRow">
              {/* ⚠️ THE SHARED `Stars` — this screen used to build its own
                  `★★★★★`.slice(0, 4.5), and JavaScript truncates a fractional
                  slice, so **4.5 drew FOUR stars** while the venue header drew
                  five for the same number. One owner in `ui.jsx` now. */}
              {r.rating != null && <Stars value={Number(r.rating)} />}
              <span className="mut">
                {new Date(r.created_at).toLocaleDateString('en-GB',
                  { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
            {/* ⚠️ ONE CHIP PER SCORE THAT EXISTS — `08d_Reviews.html` skips a
                null rather than printing a zero, and so does this. A `0` beside
                Service is a score somebody gave; a blank is a score they did
                not. */}
            {SUBS.some(([, k]) => r[k] != null) && (
              <div className="subs">
                {SUBS.filter(([, k]) => r[k] != null).map(([label, k]) => (
                  <span key={k}>{label} <b>{r[k]}</b></span>
                ))}
              </div>
            )}
            {r.body && <p className="rbody">{r.body}</p>}

            {/* ⚠️ THE RESTAURANT'S REPLY IS THE POINT OF OPENING THIS SCREEN. */}
            {r.response_body && (
              <div className="reply">
                <div className="mut">{r.restaurant_name} replied</div>
                <p>{r.response_body}</p>
              </div>
            )}

            {/* ⚠️ A TAKEN-DOWN REVIEW SAYS SO TO ITS AUTHOR. */}
            {r.status && r.status !== 'visible' && (
              <div className="mut">This review is not showing publicly.</div>
            )}
          </div>
        ))}
      </main>

      <TabBar active="profile" />
    </div>
  );
}
